import { emptySplitApi } from '@/store/emptyApi';
import type {
  CultureHubAnalyticsResponse,
  CultureHubInterventionsResponse,
} from '../types/api';

export const CULTURE_HUB_ROUTES = {
  analytics: '/api/portal/v1/culture-hub/analytics/',
  actionPlans: '/api/portal/v1/culture-hub/action-plans/',
  pillars: '/api/portal/v1/culture-hub/pillars/',
  pillarsSummary: '/api/portal/v1/culture-hub/pillars-summary/',
} as const;

interface CultureHubAnalyticsRequest {
  assessmentPeriodId: number;
  teamId?: number;
}

type CultureHubPillarsResponse = CultureHubInterventionsResponse; // temporary alias until types are regenerated

interface CultureHubPillarsRequest {
  assessmentPeriodId: number;
  teamId?: number;
}

const taggedApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: ['CultureHubAnalytics', 'CultureHubPillars', 'ActionPlan'],
});

export const cultureHubApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    getCultureHubAnalytics: builder.query<CultureHubAnalyticsResponse, CultureHubAnalyticsRequest>({
      query: ({ assessmentPeriodId, teamId }) => ({
        url: CULTURE_HUB_ROUTES.analytics,
        params: {
          assessment_period_id: assessmentPeriodId,
          scope: 'team',
          type_of: 2,
          ...(teamId ? { team_id: teamId } : {}),
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { assessmentPeriodId, teamId } = queryArgs;
        return `${endpointName}(${JSON.stringify({
          assessmentPeriodId,
          teamId: teamId ?? null,
        })})`;
      },
      providesTags: ['CultureHubAnalytics'],
    }),
    getCultureHubPillars: builder.query<CultureHubPillarsResponse, CultureHubPillarsRequest>({
      query: ({ assessmentPeriodId, teamId }) => ({
        url: CULTURE_HUB_ROUTES.pillars,
        params: {
          assessment_period_id: assessmentPeriodId,
          scope: 'team',
          type_of: 4,
          slim: 1,
          ...(teamId ? { team_id: teamId } : {}),
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { assessmentPeriodId, teamId } = queryArgs;
        return `${endpointName}(${JSON.stringify({
          assessmentPeriodId,
          teamId: teamId ?? null,
        })})`;
      },
      providesTags: ['CultureHubPillars'],
    }),
    getCultureHubPillarsSummary: builder.query<any, CultureHubPillarsRequest>({
      query: ({ assessmentPeriodId, teamId }) => ({
        url: CULTURE_HUB_ROUTES.pillarsSummary,
        params: {
          assessment_period_id: assessmentPeriodId,
          scope: 'team',
          type_of: 4,
          ...(teamId ? { team_id: teamId } : {}),
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { assessmentPeriodId, teamId } = queryArgs;
        return `${endpointName}(${JSON.stringify({
          assessmentPeriodId,
          teamId: teamId ?? null,
        })})`;
      },
      providesTags: ['CultureHubPillars'],
    }),
    getActionPlans: builder.query({
      query: ({ organizationId, assessmentPeriodId, teamId }) => ({
        url: CULTURE_HUB_ROUTES.actionPlans,
        params: {
          assessment_period_id: assessmentPeriodId,
          scope: 'team',
          team_id: teamId,
        },
        headers: {
          'x-nulogic-organization-id': organizationId,
        },
      }),
      transformResponse: (response: any) => response.action_plans || [],
      providesTags: ['ActionPlan'],
    }),

  }),
});

export const {
  useGetCultureHubAnalyticsQuery,
  useGetCultureHubPillarsQuery,
  useGetCultureHubPillarsSummaryQuery,
  useGetActionPlansQuery,
} = cultureHubApi;

